import { ChevronRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { StatusChip, type StatusChipTone } from '@/components/ui/status-chip';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { ApiError } from '@/lib/api/client';
import { useDiscoverySummaryQuery, type DiscoverySummary } from '@/lib/api/discovery';
import { attentionCard, plural, sourcesRuleText } from '@/components/discovery/discovery-logic';
import {
  coverageMetaText,
  coverageText,
  sessionsText,
  signoffAttention,
} from './module-status-logic';
import { STATUS_ROW_LINK, StatusRow } from './status-card-row';

/**
 * Discovery status on the engagement Overview: the framework, sessions
 * held, coverage, evidence awaiting review and the sign-off state, each
 * with its source, and a way into Discovery.
 *
 * Shown only when the engagement's Discovery module is on. Layout follows
 * the Overview's other cards (`HealthPostureGrid`: title row with a ghost
 * "Open" link, a two-column grid of rows) and each row is worded the way
 * the Discovery dashboard's KPI tiles are (`components/discovery/overview/
 * dashboard.tsx`, prototype `kpi()` in `docs/discovery/prototype/
 * discovery.js`). Every number is the API's summary (`GET .../discovery`);
 * nothing is counted here.
 */

const SIGNOFF_TONE: Record<string, StatusChipTone> = {
  signed: 'healthy',
  waived: 'at-risk',
  requested: 'amber',
  changes_requested: 'critical',
  reopened: 'critical',
};

export function DiscoveryStatusCard({ engagementId }: { engagementId: string }) {
  const q = useDiscoverySummaryQuery(engagementId);

  return (
    <section
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      aria-labelledby="discovery-status-title"
      data-testid="discovery-status-card"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 id="discovery-status-title" className="t-h3">
            {q.data?.framework
              ? `Discovery · ${q.data.framework.name} v${q.data.framework.version}`
              : 'Discovery'}
          </h2>
          <p className="t-meta">{caption(q.data)}</p>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/engagements/$engagementId/discovery" params={{ engagementId }}>
            Open Discovery
            <ChevronRight className="size-3" aria-hidden="true" />
          </Link>
        </Button>
      </div>

      {q.error ? (
        q.error instanceof ApiError && q.error.code === 'not_found' ? (
          <p className="text-body-sm text-ink-500">
            Discovery is not available here: the module is off for this engagement, or you do not
            have access to it.
          </p>
        ) : (
          <ErrorMessage error={q.error} />
        )
      ) : !q.data ? (
        <p className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading discovery…
        </p>
      ) : !q.data.started ? (
        <NotStarted engagementId={engagementId} />
      ) : (
        <Rows engagementId={engagementId} s={q.data} />
      )}
    </section>
  );
}

function caption(s: DiscoverySummary | undefined): string {
  if (!s) return 'Interviews, documents and the evidence they confirm';
  if (!s.started) return 'Not started';
  return `${plural(s.kpis.areas, 'area')} · ${sourcesRuleText(s)}`;
}

function NotStarted({ engagementId }: { engagementId: string }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-body-sm text-ink-500">
        Discovery has not started. Choose the framework, check the client facts and name who you
        will interview.
      </p>
      <Button asChild variant="outline" size="sm">
        <Link to="/engagements/$engagementId/discovery/setup" params={{ engagementId }}>
          Set up Discovery
        </Link>
      </Button>
    </div>
  );
}

function Rows({ engagementId, s }: { engagementId: string; s: DiscoverySummary }) {
  const k = s.kpis;
  const signoff = signoffAttention(s);
  const signoffView = signoff ? attentionCard(signoff) : null;
  const signoffTone: StatusChipTone =
    (signoff?.state ? SIGNOFF_TONE[signoff.state] : undefined) ??
    (signoff?.tone === 'ok' ? 'healthy' : 'neutral');
  const params = { engagementId };
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
      <StatusRow
        label="Sessions held"
        value={sessionsText(k)}
        meta={`${k.interviews_scheduled.toLocaleString('en-US')} scheduled`}
        source="From the session plan"
        action={
          <Link
            to="/engagements/$engagementId/discovery/sessions"
            params={params}
            className={STATUS_ROW_LINK}
          >
            Sessions
          </Link>
        }
      />
      <StatusRow
        label="Coverage"
        value={coverageText(k)}
        meta={coverageMetaText(k)}
        source="From confirmed evidence"
        action={
          <Link
            to="/engagements/$engagementId/discovery/findings"
            params={params}
            className={STATUS_ROW_LINK}
          >
            Findings
          </Link>
        }
      />
      <StatusRow
        label="Evidence awaiting review"
        value={k.awaiting_review.toLocaleString('en-US')}
        meta="AI proposals, not yet counted"
        source="From the review queue"
        ai
        action={
          <Link
            to="/engagements/$engagementId/discovery/findings"
            params={params}
            search={{ area: 'all', status: 'proposed' }}
            className={STATUS_ROW_LINK}
          >
            Review
          </Link>
        }
      />
      <StatusRow
        label="Sign-off"
        value={
          signoffView ? (
            <StatusChip tone={signoffTone} size="sm">
              {signoffView.title}
            </StatusChip>
          ) : (
            'Not available yet'
          )
        }
        meta={
          signoffView?.text ??
          'The client signs from a secure link once the requirements are ready.'
        }
        source="From the sign-off checklist"
        action={
          <Link
            to="/engagements/$engagementId/discovery/signoff"
            params={params}
            className={STATUS_ROW_LINK}
          >
            Sign-off
          </Link>
        }
      />
    </div>
  );
}
